import { Link } from "react-router-dom";
import { motion } from "framer-motion";
import { ArrowUpRight, Check, Sparkles, Database, ShieldCheck, Cloud, Code2, BarChart3, ServerCog, Palette, Workflow, Landmark, LayoutTemplate, Network, KeyRound, Bug, Lock, Eye, FileCheck } from "lucide-react";
import Reveal from "@/components/Reveal";
import TiltCard from "@/components/TiltCard";
import { SERVICES, staggerParent, fadeUp, MORE_SERVICES, SECURITY_LAYERS, CLOUD_PROVIDERS } from "@/lib/site";

const ICONS = { ai: Sparkles, crm: Database, security: ShieldCheck };
const MORE_ICONS = { Cloud, Code2, BarChart3, ServerCog, Palette, Workflow, Landmark, LayoutTemplate };
const SEC_ICONS = { Network, KeyRound, Bug, Lock, Eye, FileCheck };

function PageHeader() {
  return (
    <section className="relative overflow-hidden border-b border-white/10 pt-40 pb-24">
      <div className="absolute inset-0 grid-lines opacity-60" aria-hidden="true" />
      <div
        className="pointer-events-none absolute -top-32 left-1/3 h-[420px] w-[420px] rounded-full opacity-30 blur-[120px]"
        style={{ background: "radial-gradient(circle, rgba(0,240,255,0.3), transparent 70%)" }}
        aria-hidden="true"
      />
      <div className="relative mx-auto max-w-[1400px] px-6 lg:px-10">
        <Reveal>
          <span className="overline text-cyber">Services</span>
        </Reveal>
        <Reveal i={1}>
          <h1 data-testid="services-heading" className="mt-6 max-w-4xl font-display text-5xl font-semibold leading-[0.95] tracking-tighter text-white sm:text-6xl lg:text-7xl">
            Capabilities engineered to<br className="hidden sm:block" /> compound.
          </h1>
        </Reveal>
        <Reveal i={2}>
          <p className="mt-8 max-w-2xl font-body text-lg leading-relaxed text-white/55">
            From intelligent automation to fortified networks, we cover the full
            stack of modern enterprise engineering — delivered as living systems,
            not disposable projects.
          </p>
        </Reveal>
      </div>
    </section>
  );
}

function ServiceRows() {
  return (
    <section data-testid="services-list" className="mx-auto max-w-[1400px] px-6 lg:px-10">
      {SERVICES.map((s, idx) => {
        const Icon = ICONS[s.key];
        const flip = idx % 2 === 1;
        return (
          <div
            key={s.key}
            data-testid={`service-detail-${s.key}`}
            className="grid items-center gap-12 border-b border-white/10 py-20 lg:grid-cols-2 lg:py-28"
          >
            <Reveal className={flip ? "lg:order-2" : ""}>
              <TiltCard max={7} className="glossy relative overflow-hidden rounded-3xl">
                <img src={s.image} alt={s.title} className="h-[360px] w-full object-cover sm:h-[440px]" />
                <div className="absolute inset-0 bg-gradient-to-t from-ink-950/70 to-transparent" aria-hidden="true" />
                <span className="absolute left-6 top-6 flex h-12 w-12 items-center justify-center rounded-xl border border-cyber/40 bg-ink-950/70 text-cyber backdrop-blur [transform:translateZ(50px)]">
                  <Icon className="h-5 w-5" />
                </span>
              </TiltCard>
            </Reveal>

            <div className={flip ? "lg:order-1" : ""}>
              <Reveal>
                <span className="font-mono text-sm text-cyber/70">/ {s.index}</span>
              </Reveal>
              <Reveal i={1}>
                <h2 className="mt-4 font-display text-4xl font-semibold leading-tight tracking-tighter text-white sm:text-5xl">
                  {s.title}
                </h2>
              </Reveal>
              <Reveal i={2}>
                <p className="mt-3 font-display text-lg text-cyber/90">{s.tagline}</p>
              </Reveal>
              <Reveal i={3}>
                <p className="mt-6 max-w-lg font-body text-base leading-relaxed text-white/55">{s.summary}</p>
              </Reveal>
              <motion.ul
                variants={staggerParent}
                initial="hidden"
                whileInView="show"
                viewport={{ once: true, amount: 0.3 }}
                className="mt-8 grid gap-3 sm:grid-cols-2"
              >
                {s.points.map((p) => (
                  <motion.li key={p} variants={fadeUp} className="flex items-start gap-3 font-body text-sm text-white/70">
                    <Check className="mt-0.5 h-4 w-4 shrink-0 text-cyber" />
                    {p}
                  </motion.li>
                ))}
              </motion.ul>
            </div>
          </div>
        );
      })}
    </section>
  );
}

function Process() {
  const steps = [
    { no: "01", title: "Map the loop", body: "We audit your data, workflows and risks to find where technology creates the most leverage." },
    { no: "02", title: "Architect", body: "We design a secure, scalable blueprint — pragmatic, integration-first and built to evolve." },
    { no: "03", title: "Ship & harden", body: "We deploy in tight cycles, instrument everything and lock down the perimeter." },
    { no: "04", title: "Refine forever", body: "We monitor, measure and improve — turning your system into a compounding advantage." },
  ];
  return (
    <section className="mx-auto max-w-[1400px] px-6 py-24 lg:px-10 lg:py-32">
      <Reveal>
        <span className="overline text-cyber">How we work</span>
        <h2 className="mt-5 max-w-2xl font-display text-4xl font-semibold tracking-tighter text-white sm:text-5xl">
          A disciplined loop, end to end.
        </h2>
      </Reveal>
      <div className="mt-14 grid gap-5 md:grid-cols-4">
        {steps.map((st, i) => (
          <Reveal key={st.no} i={i}>
            <div className="group h-full rounded-2xl border border-white/10 bg-ink-850 p-7 transition-colors duration-500 hover:border-cyber/40">
              <span className="font-mono text-sm text-cyber">{st.no}</span>
              <h3 className="mt-6 font-display text-xl font-semibold tracking-tight text-white">{st.title}</h3>
              <p className="mt-3 font-body text-sm leading-relaxed text-white/50">{st.body}</p>
            </div>
          </Reveal>
        ))}
      </div>
    </section>
  );
}

function CTA() {
  return (
    <section className="border-t border-white/10 bg-ink-900">
      <div className="mx-auto flex max-w-[1400px] flex-col gap-8 px-6 py-20 lg:flex-row lg:items-center lg:justify-between lg:px-10">
        <h2 className="font-display text-3xl font-semibold tracking-tighter text-white sm:text-4xl">
          Have a system in mind?
        </h2>
        <Link
          to="/contact"
          className="group inline-flex w-max items-center gap-2 rounded-full bg-cyber px-7 py-4 font-body text-sm font-medium text-ink-950 transition-colors duration-300 hover:bg-white"
        >
          Brief us on your project
          <ArrowUpRight className="h-4 w-4 transition-transform duration-300 group-hover:translate-x-1 group-hover:-translate-y-1" />
        </Link>
      </div>
    </section>
  );
}

export default function Services() {
  return (
    <div data-testid="services-page">
      <PageHeader />
      <ServiceRows />
      <SecurityLayers />
      <CloudExpertise />
      <MoreCapabilities />
      <Process />
      <CTA />
    </div>
  );
}

function SecurityLayers() {
  return (
    <section data-testid="security-layers" className="relative overflow-hidden border-t border-white/10">
      <div
        className="pointer-events-none absolute right-0 top-0 h-[420px] w-[420px] rounded-full opacity-25 blur-[130px]"
        style={{ background: "radial-gradient(circle, rgba(0,240,255,0.3), transparent 70%)" }}
        aria-hidden="true"
      />
      <div className="relative mx-auto max-w-[1400px] px-6 py-24 lg:px-10 lg:py-32">
        <div className="max-w-2xl">
          <Reveal>
            <span className="overline text-cyber">Defense in depth</span>
            <h2 className="mt-5 font-display text-4xl font-semibold tracking-tighter text-white sm:text-5xl">
              Security measured in layers, not checkboxes.
            </h2>
          </Reveal>
          <Reveal i={1}>
            <p className="mt-6 font-body text-base leading-relaxed text-white/55">
              We protect your business at every level — from the network edge to
              your data, applications and people. Each layer is hardened,
              monitored and stress-tested independently.
            </p>
          </Reveal>
        </div>
        <div className="mt-14 grid gap-4 sm:grid-cols-2 lg:grid-cols-3">
          {SECURITY_LAYERS.map((l, i) => {
            const Icon = SEC_ICONS[l.icon] || Lock;
            return (
              <Reveal key={l.title} i={i % 3}>
                <div className="group h-full rounded-3xl glossy gloss-top p-7 transition-shadow duration-500 hover:glossy-cyan">
                  <div className="flex items-center gap-3">
                    <span className="flex h-11 w-11 items-center justify-center rounded-xl border border-cyber/25 bg-cyber/10 text-cyber shadow-[inset_0_1px_0_rgba(255,255,255,0.25)]">
                      <Icon className="h-5 w-5" />
                    </span>
                    <span className="font-mono text-xs text-white/30">L{i + 1}</span>
                  </div>
                  <h3 className="mt-5 font-display text-lg font-semibold tracking-tight text-white">{l.title}</h3>
                  <p className="mt-2 font-body text-sm leading-relaxed text-white/50">{l.body}</p>
                </div>
              </Reveal>
            );
          })}
        </div>
      </div>
    </section>
  );
}

function CloudExpertise() {
  return (
    <section data-testid="cloud-expertise" className="border-t border-white/10 bg-ink-900">
      <div className="mx-auto max-w-[1400px] px-6 py-24 lg:px-10 lg:py-32">
        <div className="grid gap-10 lg:grid-cols-2 lg:items-center">
          <Reveal>
            <span className="overline text-cyber">Every major cloud</span>
            <h2 className="mt-5 font-display text-4xl font-semibold tracking-tighter text-white sm:text-5xl">
              Fluent across all four hyperscalers.
            </h2>
            <p className="mt-6 max-w-lg font-body text-base leading-relaxed text-white/55">
              Whether you're on AWS, Azure, Google Cloud or Alibaba Cloud — or
              spread across several — we architect, migrate and operate your
              infrastructure with cost, resilience and security in mind.
            </p>
          </Reveal>
          <div className="grid grid-cols-2 gap-4">
            {CLOUD_PROVIDERS.map((c, i) => (
              <Reveal key={c.name} i={i % 2}>
                <TiltCard max={9} testid={`cloud-${i}`} className="glossy gloss-top h-full rounded-3xl p-7 transition-shadow duration-500 hover:glossy-cyan">
                  <div className="relative [transform:translateZ(30px)]">
                    <Cloud className="h-6 w-6 text-cyber" />
                    <h3 className="mt-5 font-display text-lg font-semibold tracking-tight text-white">{c.name}</h3>
                    <p className="mt-1 font-mono text-xs text-white/40">{c.note}</p>
                  </div>
                </TiltCard>
              </Reveal>
            ))}
          </div>
        </div>
      </div>
    </section>
  );
}

function MoreCapabilities() {
  return (
    <section data-testid="more-capabilities" className="border-t border-white/10 bg-ink-900">
      <div className="mx-auto max-w-[1400px] px-6 py-24 lg:px-10 lg:py-32">
        <Reveal>
          <span className="overline text-cyber">More capabilities</span>
          <h2 className="mt-5 max-w-2xl font-display text-4xl font-semibold tracking-tighter text-white sm:text-5xl">
            The rest of your technology stack, covered.
          </h2>
          <p className="mt-6 max-w-2xl font-body text-base leading-relaxed text-white/55">
            Beyond our three flagship practices, Core Loop Tech is a full-service
            partner — from cloud infrastructure and custom software to data,
            design and everything in between.
          </p>
        </Reveal>
        <div className="mt-14 grid gap-4 sm:grid-cols-2 lg:grid-cols-3">
          {MORE_SERVICES.map((m, i) => {
            const Icon = MORE_ICONS[m.icon] || Workflow;
            return (
              <Reveal key={m.key} i={i % 3}>
                <TiltCard
                  max={8}
                  testid={`capability-${m.key}`}
                  className="glossy gloss-top group h-full rounded-3xl p-7 transition-shadow duration-500 hover:glossy-cyan"
                >
                  <div className="relative [transform:translateZ(30px)]">
                    <span className="flex h-12 w-12 items-center justify-center rounded-xl border border-cyber/25 bg-cyber/10 text-cyber shadow-[inset_0_1px_0_rgba(255,255,255,0.25)]">
                      <Icon className="h-5 w-5" />
                    </span>
                    <h3 className="mt-6 font-display text-xl font-semibold tracking-tight text-white">{m.title}</h3>
                    <p className="mt-2 font-body text-sm leading-relaxed text-white/50">{m.body}</p>
                  </div>
                </TiltCard>
              </Reveal>
            );
          })}
        </div>
      </div>
    </section>
  );
}
